import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTemplates, useDeactivateTemplate } from '../hooks';
import { Button } from '@/components/ui/button';
import type { TemplateCategory } from '../types';
import { BulkAssignModal } from '../components/BulkAssignModal';

const categoryLabels: Record<TemplateCategory, { label: string; icon: string }> = {
    onboarding: { label: 'Onboarding', icon: '🟢' },
    offboarding: { label: 'Offboarding', icon: '🔴' },
    client_setup: { label: 'Client Setup', icon: '🔵' },
    audit_prep: { label: 'Audit Prep', icon: '🟡' },
    office_move: { label: 'Office Move', icon: '🟣' },
    custom: { label: 'Custom', icon: '⚪' },
};

export function TemplateListPage() {
    const [categoryFilter, setCategoryFilter] = useState('');
    const [assignOpen, setAssignOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const { data: templates, isLoading } = useTemplates(
        categoryFilter || undefined
    );
    const deactivateMutation = useDeactivateTemplate();

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Checklist Templates</h1>
                    <p className="text-sm text-muted-foreground">
                        Reusable multi-step process templates
                    </p>
                </div>
                <Link to="/checklists/templates/new">
                    <Button>+ New Template</Button>
                </Link>
            </div>

            <div className="flex gap-2">
                <button
                    className={`rounded-full px-3 py-1 text-sm ${!categoryFilter
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                    onClick={() => setCategoryFilter('')}
                >
                    All
                </button>
                {Object.entries(categoryLabels).map(([key, { label, icon }]) => (
                    <button
                        key={key}
                        className={`rounded-full px-3 py-1 text-sm ${categoryFilter === key
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                        onClick={() => setCategoryFilter(key)}
                    >
                        {icon} {label}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templates?.map((t) => {
                        const cat = categoryLabels[t.category] ?? categoryLabels.custom;
                        return (
                            <div
                                key={t.id}
                                className="rounded-lg border p-4 transition hover:shadow-sm"
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <Link
                                            to={`/checklists/templates/${t.id}/edit`}
                                            className="font-medium text-primary hover:underline"
                                        >
                                            {t.name}
                                        </Link>
                                        <p className="mt-0.5 text-xs text-muted-foreground">
                                            {cat.icon} {cat.label} · v{t.version}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (confirm('Deactivate this template?')) {
                                                deactivateMutation.mutate(t.id);
                                            }
                                        }}
                                        className="text-xs text-red-400 hover:text-red-600"
                                    >
                                        Deactivate
                                    </button>
                                </div>
                                {t.description && (
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                        {t.description}
                                    </p>
                                )}
                                <div className="mt-3">
                                    <Button size="sm" variant="outline" onClick={() => {
                                        setSelectedTemplateId(t.id);
                                        setAssignOpen(true);
                                    }}>
                                        Assign
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                    {templates?.length === 0 && (
                        <div className="col-span-full rounded-lg border border-dashed p-8 text-center">
                            <p className="text-muted-foreground">
                                No templates yet. Create your first template to get started.
                            </p>
                        </div>
                    )}
                </div>
            )}
            <BulkAssignModal 
                open={assignOpen} 
                onOpenChange={setAssignOpen} 
                initialTemplateId={selectedTemplateId} 
            />
        </div>
    );
}